import { useSyncExternalStore } from "react";

/**
 * Reports whether the component has mounted on the client.
 *
 * Returns `false` on the server and on the first client render, then `true`
 * after mount. Gate time-, timezone-, or browser-only values behind this so the
 * server HTML and the first client render stay identical and hydration does not
 * mismatch (React error #418).
 *
 * `useSyncExternalStore` rather than `useState` plus an effect: it is the one
 * hook allowed to answer differently on the server, so it hands React the
 * mismatch explicitly instead of rendering twice and repainting. Same contract
 * either way, one render pass fewer, which matters on a list that gates a
 * formatted time on every row.
 */

/** Nothing changes after the first paint, so nothing ever calls back. */
const subscribe = () => () => {};

/** In the browser. */
const onClient = () => true;

/** On the server, where no timezone can be trusted. */
const onServer = () => false;

/**
 * @description Whether this render is running in the browser.
 * @returns {boolean} `true` after the first client render, otherwise `false`.
 */
export function useHasMounted(): boolean {
  return useSyncExternalStore(subscribe, onClient, onServer);
}
